/**
 * Centerline palette → CSS colours, using macOS's *light appearance* values
 * (plans are paper: "ink" is black on the sheet even in Dark Mode).
 */
import type { AppearanceColor, AppearanceFont } from "../markup/types.js";

export interface RGBA { r: number; g: number; b: number; a: number }

/** NSColor values in sRGB (macOS 14 light appearance). */
export const SYSTEM: Record<string, RGBA> = {
  blue: { r: 0, g: 122, b: 255, a: 1 },
  green: { r: 40, g: 205, b: 65, a: 1 },
  yellow: { r: 255, g: 204, b: 0, a: 1 },
  red: { r: 255, g: 59, b: 48, a: 1 },
  orange: { r: 255, g: 149, b: 0, a: 1 },
  purple: { r: 175, g: 82, b: 222, a: 1 },
  gray: { r: 142, g: 142, b: 147, a: 1 },
  teal: { r: 89, g: 173, b: 196, a: 1 },
  magenta: { r: 255, g: 0, b: 255, a: 1 },
  black: { r: 0, g: 0, b: 0, a: 1 },
  white: { r: 255, g: 255, b: 255, a: 1 },
  clear: { r: 0, g: 0, b: 0, a: 0 },
  /** NSColor.labelColor (light): black at 85 %. */
  label: { r: 0, g: 0, b: 0, a: 0.85 },
  secondaryLabel: { r: 0, g: 0, b: 0, a: 0.5 },
  navy: { r: 0.06 * 255, g: 0.16 * 255, b: 0.31 * 255, a: 1 },
  mutcdOrange: { r: 255, g: 0.58 * 255, b: 0, a: 1 },
  mutcdRed: { r: 0.78 * 255, g: 0.09 * 255, b: 0.14 * 255, a: 1 },
  pedYellowGreen: { r: 0.8 * 255, g: 0.92 * 255, b: 0, a: 1 },
  accessibleBlue: { r: 0, g: 0.27 * 255, b: 0.62 * 255, a: 1 },
};

/** `AppearanceColor.nsColor`. */
export function paletteRGBA(c: AppearanceColor): RGBA {
  switch (c) {
    case "ink": return SYSTEM.label!;
    case "navy": return SYSTEM.navy!;
    default: return SYSTEM[c] ?? SYSTEM.black!;
  }
}
/** `AppearanceColor.pdfColor`: ink is pure black. */
export function palettePdfRGBA(c: AppearanceColor): RGBA {
  return c === "ink" ? SYSTEM.black! : paletteRGBA(c);
}

export function css(c: RGBA, alpha?: number): string {
  const a = alpha ?? c.a;
  return `rgba(${Math.round(c.r)}, ${Math.round(c.g)}, ${Math.round(c.b)}, ${+a.toFixed(4)})`;
}
/** NSColor.withAlphaComponent — replaces the alpha. */
export const withAlpha = (c: RGBA, a: number): string => css(c, a);
export const palette = (c: AppearanceColor, alpha?: number) => css(paletteRGBA(c), alpha);

/** "#RRGGBB" → RGBA (TextBoxDetails.fillHex / textHex). */
export function hexRGBA(hex: string | undefined): RGBA | null {
  if (!hex) return null;
  const m = /^#?([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(hex.trim());
  if (!m) return null;
  const v = parseInt(m[1]!, 16);
  return { r: (v >> 16) & 255, g: (v >> 8) & 255, b: v & 255, a: m[2] ? parseInt(m[2], 16) / 255 : 1 };
}

/**
 * Faces: the Mac face first (present on Macs and many PCs), then an open
 * metric-compatible fallback. Bold/italic go in the CSS font shorthand.
 */
export const FONT_FACES: Record<AppearanceFont, { family: string; bold: boolean; italic?: boolean }> = {
  helvetica: { family: "Helvetica, Arial, 'Liberation Sans', sans-serif", bold: false },
  helveticaBold: { family: "Helvetica, Arial, 'Liberation Sans', sans-serif", bold: true },
  avenir: { family: "Avenir, 'Avenir Next', 'Nunito Sans', 'Segoe UI', sans-serif", bold: false },
  menlo: { family: "Menlo, Consolas, 'DejaVu Sans Mono', monospace", bold: false },
  avenirHeavy: { family: "Avenir, 'Avenir Next', 'Nunito Sans', 'Segoe UI', sans-serif", bold: true },
  futuraBold: { family: "Futura, 'Century Gothic', 'URW Gothic', sans-serif", bold: true },
  timesBold: { family: "Times, 'Times New Roman', 'Liberation Serif', serif", bold: true },
  georgiaBold: { family: "Georgia, 'DejaVu Serif', serif", bold: true },
  courierBold: { family: "Courier, 'Courier New', 'Liberation Mono', monospace", bold: true },
  americanTypewriter: { family: "'American Typewriter', 'Courier New', serif", bold: true },
  impact: { family: "Impact, 'Arial Black', 'Liberation Sans', sans-serif", bold: false },
  times: { family: "Times, 'Times New Roman', 'Liberation Serif', serif", bold: false },
  courier: { family: "Courier, 'Courier New', 'Liberation Mono', monospace", bold: false },
  georgia: { family: "Georgia, 'DejaVu Serif', serif", bold: false },
  arial: { family: "Arial, Helvetica, 'Liberation Sans', sans-serif", bold: false },
  verdana: { family: "Verdana, 'DejaVu Sans', sans-serif", bold: false },
};

export function cssFont(font: AppearanceFont, size: number, opts: { bold?: boolean; italic?: boolean } = {}): string {
  const face = FONT_FACES[font] ?? FONT_FACES.helvetica;
  const bold = face.bold || opts.bold;
  return `${opts.italic ? "italic " : ""}${bold ? "bold " : ""}${+size.toFixed(3)}px ${face.family}`;
}
